import React from 'react'
import { useParams } from 'react-router-dom'

import Stack from '@mui/material/Stack'

import { ElaraErrorBoundary } from '@/components/elara/ElaraErrorBoundary'
import { ElaraPanel } from '@/components/elara/ElaraPanel'
import useMedconnectDevices from '@/hooks/useMedconnectDevices'
import { useIsPds } from '@/hooks/useStaffRole'
import { DiagnosticAllotment } from '@/layouts/diagnostic/DiagnosticAllotment'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { ElaraRealtimeProvider } from '@/providers/elara/ElaraRealtimeProvider'
import { useElaraRealtimeLicenseEnabled } from '@/providers/elara/hooks/useElaraRealtimeLicenseEnabled'
import { isStaff } from '@/utils/type-guards'
import ChimeMeeting from '@/pages/diagnostic/containers/ChimeMeeting'
import PatientConsentDialog from '@/pages/diagnostic/components/PatientConsentDialog'

import { useCreateEmergencyConsultation } from '../model/useCreateEmergencyConsultation'
import { useEmergencyConsultationLifecycle } from '../model/useEmergencyConsultationLifecycle'
import { useEmergencyConsultationState } from '../model/useEmergencyConsultationState'
import { useEmergencyPageSetup } from '../model/useEmergencyPageSetup'
import { useEmergencyRoom } from '../model/useEmergencyRoom'
import { useEmergencyRoomLifecycle } from '../model/useEmergencyRoomLifecycle'
import { useEmergencySidebarMenu } from '../model/useEmergencySidebarMenu'
import { useJoinEmergencyMeeting } from '../model/useJoinEmergencyMeeting'
import { usePatientConsentDialog } from '../model/usePatientConsentDialog'
import { EmergencyContent } from './EmergencyContent'
import { EmergencyElaraNavigationActions } from './EmergencyElaraNavigationActions'
import { EmergencyInactiveView } from './EmergencyInactiveView'
import { EmergencySidebar } from './EmergencySidebar'
import { Topbar } from './Topbar'

export function EmergencyPage() {
  const { roomId } = useParams()

  const authUser = useAppSelector(selectAuthUser)
  const isPds = useIsPds()

  // State hooks
  const emergencyRoom = useEmergencyRoom(roomId)
  const consultationState = useEmergencyConsultationState(emergencyRoom)
  const { sidebarMenuItems, selectedSidebarMenuItem, setSelectedSidebarMenuItem } = useEmergencySidebarMenu(emergencyRoom)

  // Action hooks
  const { handleSelectPatient } = useCreateEmergencyConsultation({ emergencyRoom })
  const { handleJoinMeeting, isStreamer } = useJoinEmergencyMeeting({
    consultationRoom: consultationState.consultationRoom,
    roomId,
  })
  const consentDialog = usePatientConsentDialog({
    newlyCreatedConsultationId: consultationState.newlyCreatedConsultationId,
  })

  // Lifecycle hooks
  useEmergencyPageSetup()
  useEmergencyRoomLifecycle({
    consultationRoom: consultationState.consultationRoom,
    emergencyConsultation: consultationState.emergencyConsultation,
    emergencyRoom,
    newlyCreatedConsultationId: consultationState.newlyCreatedConsultationId,
    roomId,
  })
  useEmergencyConsultationLifecycle({
    emergencyConsultation: consultationState.emergencyConsultation,
    emergencyConsultationId: consultationState.emergencyConsultationId,
  })

  const hasElaraRealtimeLicense = useElaraRealtimeLicenseEnabled()

  // Device integration
  useMedconnectDevices(isPds, consultationState.emergencyConsultationId ?? '')

  // Guard: require emergency room and staff user
  if (!emergencyRoom || !isStaff(authUser)) return null

  // Show inactive view when PDS is not in room (for non-PDS users)
  if (!isPds && emergencyRoom.status === 'inactive') {
    return <EmergencyInactiveView emergencyRoom={emergencyRoom} />
  }

  const emergencyConsultationId = consultationState.emergencyConsultationId

  const elaraWrapper = hasElaraRealtimeLicense && emergencyConsultationId
    ? (children: React.ReactNode) => <ElaraRealtimeProvider consultationId={emergencyConsultationId}>{children}</ElaraRealtimeProvider>
    : (children: React.ReactNode) => <>{children}</>

  return elaraWrapper(
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <Topbar emergencyRoom={emergencyRoom} />
      {emergencyRoom.currentConsultation && (
        <EmergencySidebar
          emergencyRoom={emergencyRoom}
          sidebarMenuItems={sidebarMenuItems}
          selectedSidebarMenuItem={selectedSidebarMenuItem}
          setSelectedSidebarMenuItem={setSelectedSidebarMenuItem}
        />
      )}
      <DiagnosticAllotment defaultSizes={[60, 40]}>
        <DiagnosticAllotment.Pane>
          <div className="h-full overflow-y-auto">
            <EmergencyContent
              consultationRoom={consultationState.consultationRoom}
              emergencyConsultation={consultationState.emergencyConsultation}
              emergencyRoom={emergencyRoom}
              isCreatingConsultation={consultationState.isCreatingConsultation}
              isStreamer={isStreamer}
              onSelectPatient={handleSelectPatient}
              selectedSidebarMenuItem={selectedSidebarMenuItem}
            />
          </div>
        </DiagnosticAllotment.Pane>
        <DiagnosticAllotment.Pane>
          <div className="h-full flex [&>div]:flex-1">
            <ChimeMeeting joinMeeting={handleJoinMeeting} />
          </div>
        </DiagnosticAllotment.Pane>
      </DiagnosticAllotment>
      {consentDialog.shouldShowConsentDialog && (
        <PatientConsentDialog
          open={consentDialog.isConsentDialogOpen}
          onClose={consentDialog.handleConsentResponse}
          consultationId={emergencyRoom.currentConsultation}
          showTreatingPhysician={consentDialog.showTreatingPhysician}
        />
      )}
      {hasElaraRealtimeLicense && emergencyConsultationId && (
        <ElaraErrorBoundary>
          <ElaraPanel />
          <EmergencyElaraNavigationActions
            selectedMenuItem={selectedSidebarMenuItem}
            onSelectMenuItem={setSelectedSidebarMenuItem}
            sidebarMenuItems={sidebarMenuItems}
          />
        </ElaraErrorBoundary>
      )}
    </Stack>
  )
}
